import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import TradesTable from "../trade/TradesTable";
import { getBotConfig, getBotState } from "../utils/botUtils";
import { toast } from "react-toastify";
import { Accordion, Button, Checkbox, Icon } from "semantic-ui-react";
import BotConfig from "./BotConfig";
import BotState from "./BotState";
import BotStats from "./BotStats";
import ClosedTrades from "./ClosedTrades";
import axios from "axios/index";
import { getAuthHttpHeaders } from "../auth";
import PausedIcon from "./PausedIcon";
import HangingIcon from "./HangingIcon";
import BotStatsCandles from "./BotStatsCandles";
import AddSimGroup from "../sim/AddSimGroup";
import ErrorIcon from "./ErrorIcon";
import SuspiciousCcysIcon from "./SuspiciousCcysIcon";
import { avatar } from "../utils/avatar";

@inject("store")
@observer
class BotInstance extends Component {
  state = { runSim: false };

  load() {
    const {
      store: { isBotSectionExpanded },
    } = this.props;

    if (this.closedTradesComponent && isBotSectionExpanded("closedTrades")) {
      this.closedTradesComponent.wrappedInstance.load();
    }
  }

  toggleEnabled = async bi => {
    if (!window.confirm(`Are you sure you want to ${bi.enabled ? "disable" : "enable"} this bot?`)) {
      return;
    }

    bi.enabled = !bi.enabled;
    this.forceUpdate();

    let axiosRequest = axios.create({
      baseURL: "api",
      timeout: 30 * 1000,
      headers: {
        Accept: "application/json",
        ...getAuthHttpHeaders(),
      },
    });

    try {
      await axiosRequest.put(`botInstance/${bi.id}/setEnabled`, { enabled: bi.enabled });
    } catch (e) {
      alert(e);
    }

    this.props.onRefresh();
  };

  runSim = async () => {
    const { botInstance: bi } = this.props;

    if (!window.confirm(`Are you sure you want to run sim for this bot?`)) {
      return;
    }
    let axiosRequest = axios.create({
      baseURL: "api",
      timeout: 30 * 1000,
      headers: {
        Accept: "application/json",
        ...getAuthHttpHeaders(),
      },
    });

    try {
      await axiosRequest.post(`sim/run`, { botId: bi.id });
    } catch (e) {
      alert(e);
    }
  };

  render() {
    const {
      store: {
        zoneId,
        isBotSectionExpanded,
        toggleBotSectionExpanded,
        authStore: { userId },
      },
      botInstance: bi,
      usedSuspiciousCcys,
    } = this.props;

    const { runSim } = this.state;

    const state = getBotState(bi);
    const config = getBotConfig(bi);

    const botAvatar = avatar(bi.id, 42, { verticalAlign: "middle", marginLeft: "15px", marginRight: "15px" });

    return (
      <div>
        <h2>
          <Checkbox
            toggle
            checked={bi.enabled}
            onClick={() => this.toggleEnabled(bi)}
            style={{
              marginRight: "5px",
            }}
            title={bi.enabled ? "Disable" : "Enable"}
            disabled={bi.userId !== userId}
          />
          {botAvatar} {bi.type} {bi.id} {!bi.enabled && "(Disabled)"}
          <PausedIcon botInstance={bi} />
          <ErrorIcon botInstance={bi} />
          <HangingIcon botInstance={bi} />
          <SuspiciousCcysIcon usedSuspiciousCcys={usedSuspiciousCcys} />
        </h2>
        <div style={{ marginBottom: 5 }}>
          <a
            href={"log-bots/" + bi.id + ".log"}
            target={"log-bot-" + bi.id}
            style={{ fontWeight: "bold", marginRight: 5 }}
          >
            View Log
          </a>&nbsp;
          {!runSim && (
            <Button basic size="small" onClick={() => this.setState({ runSim: true })}>
              Run Sim
            </Button>
          )}
          {runSim && (
            <AddSimGroup
              botId={bi.id}
              onSuccess={() => {
                this.setState({ runSim: false });
                toast.info("Sim started");
              }}
              onClose={() => this.setState({ runSim: false })}
            />
          )}
        </div>
        <Accordion fluid styled>
          <Accordion.Title active={isBotSectionExpanded("config")} onClick={() => toggleBotSectionExpanded("config")}>
            <Icon name="dropdown" />Config
          </Accordion.Title>
          <Accordion.Content active={isBotSectionExpanded("config")}>
            <BotConfig
              key={bi.id}
              botInstance={bi}
              editable={bi.userId === userId}
              onRefresh={this.props.onRefresh}
              usedSuspiciousCcys={usedSuspiciousCcys}
            />
          </Accordion.Content>

          <Accordion.Title active={isBotSectionExpanded("state")} onClick={() => toggleBotSectionExpanded("state")}>
            <Icon name="dropdown" />
            State (Tick {state.tickNr})
            <ErrorIcon botInstance={bi} />
          </Accordion.Title>
          <Accordion.Content active={isBotSectionExpanded("state")}>
            <BotState botInstance={bi} />
          </Accordion.Content>

          <Accordion.Title active={isBotSectionExpanded("stats")} onClick={() => toggleBotSectionExpanded("stats")}>
            <Icon name="dropdown" />
            Stats
          </Accordion.Title>
          <Accordion.Content active={isBotSectionExpanded("stats")}>
            {isBotSectionExpanded("stats") && <BotStats botInstance={bi} />}
          </Accordion.Content>
          <Accordion.Title
            active={isBotSectionExpanded("statsCandles")}
            onClick={() => toggleBotSectionExpanded("statsCandles")}
          >
            <Icon name="dropdown" />
            Candlestick chart stats
          </Accordion.Title>
          <Accordion.Content active={isBotSectionExpanded("statsCandles")}>
            {isBotSectionExpanded("statsCandles") && <BotStatsCandles botInstance={bi} />}
          </Accordion.Content>

          <Accordion.Title
            active={isBotSectionExpanded("openTrades")}
            onClick={() => toggleBotSectionExpanded("openTrades")}
          >
            <Icon name="dropdown" />
            Open Trades ({state.tradeList && state.tradeList.length})
          </Accordion.Title>
          <Accordion.Content active={isBotSectionExpanded("openTrades")}>
            <TradesTable
              trades={state.tradeList && state.tradeList.slice().reverse()}
              botId={bi.id}
              botConfig={config}
              exchangeType={bi.exchangeType}
              zoneId={zoneId}
            />
          </Accordion.Content>

          <Accordion.Title
            active={isBotSectionExpanded("closedTrades")}
            onClick={() => toggleBotSectionExpanded("closedTrades")}
          >
            <Icon name="dropdown" />
            Closed Trades ({bi.closedTradesCount})
          </Accordion.Title>
          <Accordion.Content active={isBotSectionExpanded("closedTrades")}>
            {isBotSectionExpanded("closedTrades") && (
              <ClosedTrades
                key={bi.id}
                ref={i => (this.closedTradesComponent = i)}
                botId={bi.id}
                botConfig={config}
                exchangeType={bi.exchangeType}
              />
            )}
          </Accordion.Content>
        </Accordion>
      </div>
    );
  }
}

export default BotInstance;



// WEBPACK FOOTER //
// ./src/bot/BotInstance.js